import { TFunction } from 'i18next';
import { KeywordsData, TagsResponse } from '../../../shared/models/tags/TagsResponse';
import { TAG_NAME } from '../../../shared/utils/urls';

export type MetadataBadgeVariant = 'success' | 'danger' | 'secondary';

/**
 * Whether the selected firm/brand was found among a page's verified brands.
 * When firmName is a tag, the page counts as mentioned if ANY of the tag's member keywords was
 * found — brands holds normalized keywords, never the tag label. firmName for a tag carries a '#'
 * prefix (see getTagLabel), so we match it back to the tag by '#' + label.
 */
export const isFirmMentionedInBrands = (
  brands: string[] | null | undefined,
  firmName: string,
  tags?: TagsResponse[] | null
): boolean => {
  if (!brands || brands.length === 0) return false;
  const tag = tags?.find((tg) => `#${tg.label}` === firmName);
  if (tag) {
    return brands.includes(`${TAG_NAME}${tag.id}`);
  }
  return brands.includes(firmName);
};

/**
 * Brand names for the source pages. Without the grouping addon a tag is one brand, keyed by the tag — the
 * keyword list does not name those keys. With the addon `useGetTags` returns nothing and the list is complete.
 */
export const withTagBrandNames = <T extends KeywordsData>(keywords: T[] | undefined, tags: TagsResponse[]): T[] => [
  ...(Array.isArray(keywords) ? keywords : []),
  ...tags.map(
    (tag) =>
      ({ id: -tag.id, keyword: `#${tag.label}`, normalizedKeyword: `${TAG_NAME}${tag.id}`, monitorId: 0 }) as T
  ),
];

export interface BrandMentionStatus {
  variant: MetadataBadgeVariant;
  statusText: string;
  tooltipText: string;
}

// Video platforms (the answer lives in the video/transcript, not the fetched HTML). Used to hide
// the per-domain "Brand Mentions per Source" chart; the backend verifies these via LLM web search.
// Kept in sync with VIDEO_DOMAINS in llm-worker's sourceClassifier.
const VIDEO_DOMAINS = [
  'youtube.com',
  'youtu.be',
  'vimeo.com',
  'dailymotion.com',
  'twitch.tv',
  'tiktok.com',
  'wistia.com',
  'loom.com',
  'rumble.com',
  'kick.com',
  'bilibili.com',
  'odysee.com',
  'brightcove.com',
  'jwplayer.com',
  'vevo.com',
  'streamable.com',
  'vidyard.com',
  'dacast.com',
  'sproutvideo.com',
  'kaltura.com',
  'fastpix.io',
  'uscreen.tv',
  'spotlightr.com',
  'muse.ai',
  'bunny.net',
  'peertube.social',
  'douyin.com',
  'youku.com',
  'rutube.ru',
  'livestream.com',
];

export const isVideoSource = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    const withProtocol = url.startsWith('http') ? url : `https://${url}`;
    const h = new URL(withProtocol).hostname.replace(/^www\./, '').toLowerCase();
    return VIDEO_DOMAINS.some((p) => h === p || h.endsWith('.' + p) || h.startsWith(p));
  } catch {
    return false;
  }
};

export const getVerifiedBrandStatus = (
  isVerified: boolean | undefined,
  brandMentioned: boolean | null | undefined,
  firmName: string,
  t: TFunction
): BrandMentionStatus => {
  if (isVerified === true && brandMentioned === true) {
    return {
      variant: 'success',
      statusText: t('sourcesOwnBrandMentioned'),
      tooltipText: t('sourcesOwnBrandTooltipMentioned', { firmName }),
    };
  }

  if (isVerified === true) {
    return {
      variant: 'danger',
      statusText: t('sourcesOwnBrandNotMentioned'),
      tooltipText: t('sourcesOwnBrandTooltipNotMentioned', { firmName }),
    };
  }

  return {
    variant: 'secondary',
    statusText: t('sourcesOwnBrandNotChecked'),
    tooltipText: t('sourcesOwnBrandTooltipNotChecked'),
  };
};
